import { useEffect, useState } from 'react';
import Modal from '@/components/base/Modal';
import { supabase } from '@/lib/supabase';

interface AnnouncementRow {
  id: string;
  title: string;
  message: string | null;
  group_id: string | null;
  date: string | null;
  priority: string;
  visible: boolean;
}

interface GroupInfo {
  id: string;
  name: string;
  color: string | null;
}

interface DuyuruModalProps {
  open: boolean;
  onClose: () => void;
  announcement: AnnouncementRow | null;
  groups: GroupInfo[];
}

export default function DuyuruModal({ open, onClose, announcement, groups }: DuyuruModalProps) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [messageLen, setMessageLen] = useState(0);

  useEffect(() => {
    if (!open) {
      setError(null);
      setSaving(false);
    }
    setMessageLen((announcement?.message ?? '').length);
  }, [open, announcement]);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const title = (fd.get('title') as string || '').trim();
    const message = (fd.get('message') as string || '').trim() || null;
    const group_id = (fd.get('group_id') as string || '').trim() || null;
    const priority = (fd.get('priority') as string || '').trim() || 'dusuk';
    const visible = fd.get('visible') === 'on';

    if (!title) {
      setError('Lütfen duyuru başlığı girin.');
      return;
    }

    setError(null);
    setSaving(true);

    try {
      const payload = {
        title,
        message,
        group_id,
        priority,
        visible,
        date: new Date().toISOString().slice(0, 10),
      };

      if (announcement) {
        const { error: updateError } = await supabase
          .from('zehrahoca_announcements')
          .update(payload)
          .eq('id', announcement.id);
        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('zehrahoca_announcements')
          .insert(payload);
        if (insertError) throw insertError;
      }

      form.reset();
      window.dispatchEvent(new Event('zehrahoca:announcements-changed'));
      onClose();
    } catch (err) {
      setError((err as Error).message || 'Duyuru kaydedilirken bir hata oluştu.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={announcement ? 'Duyuru Düzenle' : 'Yeni Duyuru Ekle'}
      subtitle={announcement ? 'Duyuru bilgilerini güncelleyin.' : 'Yeni bir duyuru oluşturun.'}
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Başlık</label>
            <input
              type="text"
              name="title"
              defaultValue={announcement?.title ?? ''}
              required
              placeholder="Örn: Ara Tatil Ders Programı"
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>

          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Mesaj</label>
            <textarea
              name="message"
              defaultValue={announcement?.message ?? ''}
              rows={4}
              maxLength={500}
              onChange={(e) => setMessageLen(e.target.value.length)}
              placeholder="Duyuru metnini yazın..."
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
            <p className="mt-1 text-right text-xs text-foreground-400">{messageLen}/500 karakter</p>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Öncelik</label>
            <select
              name="priority"
              defaultValue={announcement?.priority ?? 'dusuk'}
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100 cursor-pointer"
            >
              <option value="dusuk">Düşük</option>
              <option value="orta">Orta</option>
              <option value="yuksek">Yüksek</option>
            </select>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Hedef Grup</label>
            <select
              name="group_id"
              defaultValue={announcement?.group_id ?? ''}
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100 cursor-pointer"
            >
              <option value="">Tüm gruplar</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
          </div>
        </div>

        <label className="flex cursor-pointer items-center gap-2 rounded-lg bg-background-50 p-4 text-sm text-foreground-800">
          <input
            type="checkbox"
            name="visible"
            defaultChecked={announcement?.visible ?? true}
            className="h-4 w-4 rounded border-background-300 text-primary-500 focus:ring-primary-400"
          />
          Yayınla (öğrenci panelinde görünsün)
        </label>

        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3.5 py-3 text-sm text-red-700">
            <i className="ri-error-warning-line mt-0.5 text-base" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex items-center justify-end gap-3 border-t border-background-200 pt-5">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-4 py-2.5 text-sm font-semibold text-foreground-700 transition hover:bg-background-100 cursor-pointer whitespace-nowrap"
          >
            İptal
          </button>
          <button
            type="submit"
            disabled={saving}
            className="rounded-lg bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-600 disabled:opacity-70 cursor-pointer whitespace-nowrap"
          >
            {saving ? 'Kaydediliyor...' : announcement ? 'Güncelle' : 'Kaydet'}
          </button>
        </div>
      </form>
    </Modal>
  );
}